/**
 * Utility functions for cleaning and normalizing URLs
 */

/**
 * Clean YouTube URL by removing playlist parameters and other unnecessary query parameters
 * Examples:
 * - https://www.youtube.com/watch?v=A7rKh5BnXss&list=RDIzTJQiFoNGE&index=3 -> https://www.youtube.com/watch?v=A7rKh5BnXss
 * - https://youtu.be/A7rKh5BnXss?t=123 -> https://www.youtube.com/watch?v=A7rKh5BnXss
 */
export function cleanYouTubeUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    let videoId = '';

    // Extract video ID from various YouTube URL formats
    if (urlObj.hostname === 'youtu.be') {
      // Short URL format: https://youtu.be/VIDEO_ID
      videoId = urlObj.pathname.substring(1); // Remove leading slash
    } else if (urlObj.hostname.includes('youtube.com')) {
      // Standard format: https://www.youtube.com/watch?v=VIDEO_ID
      videoId = urlObj.searchParams.get('v') || '';
    }

    // Validate video ID (should be 11 characters, alphanumeric + - and _)
    if (videoId && /^[\w-]{11}$/.test(videoId)) {
      const cleanUrl = `https://www.youtube.com/watch?v=${videoId}`;
      // console.log(`Cleaned YouTube URL: ${url} -> ${cleanUrl}`);
      return cleanUrl;
    }

    console.warn(`Could not extract video ID from YouTube URL: ${url}`);
    return url;
  } catch (error) {
    console.warn(`Failed to clean YouTube URL ${url}:`, error);
    return url;
  }
}

/**
 * Check if a URL is a YouTube URL
 */
export function isYouTubeUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.includes('youtube.com') || urlObj.hostname === 'youtu.be';
  } catch {
    return false;
  }
}

/**
 * Clean URL based on platform
 */
export function cleanUrl(url: string, platform?: string): string {
  if (!url) return url;
  
  // Auto-detect platform if not provided
  if (!platform) {
    if (isYouTubeUrl(url)) {
      platform = 'youtube';
    }
  }
  
  switch (platform) {
    case 'youtube':
      return cleanYouTubeUrl(url);
    default:
      return url;
  }
}
